import { useState, useEffect, useRef } from "react"
import api from '../../utils/api'

import Input from "../../shared/components/form/input"
import Button from "../../shared/components/Button/Button"
import { FiRefreshCw } from 'react-icons/fi'

/* Hooks */
import useFlashMessage from '../../hooks/useFlashMessage'
import useMyPermission from '../../hooks/useMyPermission'
import TimestampToString from '../../shared/functions/TimestampToString'

import './NcmSearchModal.css'

function NcmSearchModal({ onSelect, initialQuery }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const [query, setQuery] = useState(initialQuery || '')
    const [results, setResults] = useState([])
    const [total, setTotal] = useState(0)
    const [searching, setSearching] = useState(false)
    const timerRef = useRef(null)
    const { setFlashMessage } = useFlashMessage()
    const { checkPermission } = useMyPermission()
    const { timeStampToDDMMAAAA } = TimestampToString()

    const [ncmLastSyncedAt, setNcmLastSyncedAt] = useState(null)
    const [myPermissionEdit, setMyPermissionEdit] = useState(false)
    const [syncing, setSyncing] = useState(false)

    // Não trava a busca se o usuário não tiver fiscal_view/fiscal_edit — só esconde
    // a área de sincronização nesse caso (a busca em si não exige permissão fiscal).
    function loadSyncInfo() {
        api.get('/user/get/permission', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }).then((response) => {
            setMyPermissionEdit(checkPermission(response.data.permission, 'fiscal_edit'))
        })
        api.get('/fiscal/config', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }).then((response) => {
            setNcmLastSyncedAt(response.data.fiscalConfig?.ncmLastSyncedAt || null)
        }).catch(() => {})
    }

    function fetchResults(q) {
        setSearching(true)
        api.get(`/fiscal/ncm/search?q=${encodeURIComponent(q)}`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            setResults(response.data.ncmList || [])
            setTotal(response.data.total || 0)
            setSearching(false)
        }).catch(() => setSearching(false))
    }

    useEffect(() => {
        loadSyncInfo()
        fetchResults(initialQuery || '')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function syncNow() {
        setSyncing(true)
        let msgType = 'success'
        const data = await api.post('/fiscal/ncm/sync', {}, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            return response.data
        }).catch((err) => {
            msgType = 'error'
            return err.response.data
        })

        setFlashMessage(data.message, msgType)
        setSyncing(false)
        if (msgType !== 'error') {
            loadSyncInfo()
        }
    }

    function handleSearchChange(e) {
        const q = e.target.value
        setQuery(q)

        setSearching(true)
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => fetchResults(q), 350)
    }

    return (
        <div className="ncm-search">
            <div className="ncm-search-header">
                <p className="ncm-search-title">Buscar NCM</p>
                <span className="ncm-search-title-accent" />
                <p className="ncm-search-subtitle">Tabela oficial (Receita Federal/Siscomex). Confirme sempre com seu contador antes de emitir de verdade.</p>
                {initialQuery && (
                    <p className="ncm-search-suggestion-hint">Sugestão baseada na categoria do produto ("{initialQuery}") — refine a busca se precisar.</p>
                )}
            </div>

            {myPermissionEdit && (
                <div className="ncm-search-sync">
                    <span className="ncm-search-sync-text">
                        Última sincronização: {ncmLastSyncedAt ? timeStampToDDMMAAAA(ncmLastSyncedAt) : 'nunca'}
                    </span>
                    <Button variant="config" icon={FiRefreshCw} className={syncing ? 'ncm-sync-icon-spin' : ''} onClick={syncNow} disabled={syncing}>{syncing ? 'Sincronizando...' : 'Sincronizar Agora'}</Button>
                </div>
            )}

            <Input type="text" placeholder="Buscar por descrição (camisa, calça, tecido...) ou código" value={query} handleOnChange={handleSearchChange} />

            {!searching && results.length > 0 && total > results.length && (
                <p className="ncm-search-count">Mostrando {results.length} de {total} resultados — refine a busca pra achar mais rápido.</p>
            )}

            <div className="ncm-search-results">
                {searching && <p className="ncm-search-empty">Buscando...</p>}
                {!searching && results.length === 0 && (
                    <p className="ncm-search-empty">{query ? `Nenhum NCM encontrado para "${query}".` : 'Nenhum NCM cadastrado ainda.'}</p>
                )}
                {results.map((n) => (
                    <div key={n.code} className="ncm-search-item" onClick={() => onSelect(n)}>
                        <span className="ncm-search-item-code">{n.code}</span>
                        <span className="ncm-search-item-desc">{n.description}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default NcmSearchModal
